export const DEFAULT_SOURCE_OF_FUND_OPTIONS = [
  { key: "salary", label: "Salary", requiresSpecify: false, enabled: true, sortOrder: 0 },
  { key: "businessProfit", label: "Business Profit", requiresSpecify: false, enabled: true, sortOrder: 1 },
  { key: "salesProceed", label: "Sales Proceed", requiresSpecify: false, enabled: true, sortOrder: 2 },
  { key: "rentalIncome", label: "Rental Income", requiresSpecify: false, enabled: true, sortOrder: 3 },
  {
    key: "financialSupport",
    label: "Financial support from others",
    requiresSpecify: false,
    enabled: true,
    sortOrder: 4,
  },
  { key: "pension", label: "Pension", requiresSpecify: false, enabled: true, sortOrder: 5 },
  { key: "investment", label: "Investment", requiresSpecify: false, enabled: true, sortOrder: 6 },
  { key: "sales", label: "Sales/Commission", requiresSpecify: false, enabled: true, sortOrder: 7 },
  { key: "salesRenue", label: "Sales Revenue", requiresSpecify: false, enabled: true, sortOrder: 8 },
  { key: "inheritance", label: "Inheritance", requiresSpecify: false, enabled: true, sortOrder: 9 },
  { key: "gift", label: "Gift", requiresSpecify: false, enabled: true, sortOrder: 10 },
  { key: "soldOfProperty", label: "Sold of Property", requiresSpecify: false, enabled: true, sortOrder: 11 },
  { key: "saving", label: "Saving", requiresSpecify: false, enabled: true, sortOrder: 12 },
  {
    key: "other",
    label: "Others",
    requiresSpecify: true,
    enabled: true,
    sortOrder: 13,
  },
];

export const slugifySourceOfFundKey = (label, existingKeys = new Set()) => {
  const base = String(label || "")
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((part, index) =>
      index === 0
        ? part.toLowerCase()
        : part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
    )
    .join("");

  if (!base) return "option";

  let key = base;
  let counter = 2;
  while (existingKeys.has(key)) {
    key = `${base}${counter}`;
    counter += 1;
  }
  return key;
};

export const resolveSourceOfFundLabel = (option = {}) => {
  const label = String(option.label || "").trim();
  if (!label) return label;

  if (
    option.key === "other" &&
    label.toLowerCase() === "others, please specify"
  ) {
    return "Others";
  }

  return label;
};

export const normalizeSourceOfFundOption = (option = {}, index = 0, existingKeys = new Set()) => {
  const key =
    String(option.key || "").trim() ||
    slugifySourceOfFundKey(String(option.label || "").trim(), existingKeys);

  const label = resolveSourceOfFundLabel({ ...option, key });
  if (!label) return null;

  existingKeys.add(key);

  return {
    key,
    label,
    requiresSpecify: Boolean(option.requiresSpecify),
    enabled: option.enabled !== false,
    sortOrder:
      option.sortOrder === 0 || option.sortOrder
        ? Number(option.sortOrder)
        : index,
  };
};

export const normalizeSourceOfFundOptions = (options) => {
  const source = Array.isArray(options) && options.length ? options : DEFAULT_SOURCE_OF_FUND_OPTIONS;
  const existingKeys = new Set();
  return source
    .map((option, index) => normalizeSourceOfFundOption(option, index, existingKeys))
    .filter(Boolean)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((option, index) => ({ ...option, sortOrder: index }));
};

export const moveSourceOfFundOption = (options, fromIndex, direction) => {
  if (!Array.isArray(options)) return options;
  const index = Number(fromIndex);
  const nextIndex = index + Number(direction);
  if (!Number.isInteger(index) || index < 0 || index >= options.length) return options;
  if (nextIndex < 0 || nextIndex >= options.length) return options;

  const next = [...options];
  const [item] = next.splice(index, 1);
  next.splice(nextIndex, 0, item);
  return normalizeSourceOfFundOptions(
    next.map((row, sortOrder) => ({ ...row, sortOrder }))
  );
};

export const getEnabledSourceOfFundOptions = (options) =>
  normalizeSourceOfFundOptions(options).filter((option) => option.enabled);

export const getIncomeSourceSpecifyField = (key) =>
  key === "other" ? "otherSpecify" : `${key}Specify`;

export const serializeSourceOfFundOptions = (options = []) =>
  normalizeSourceOfFundOptions(options).map((option, index) => ({
    key: option.key,
    label: option.label,
    requiresSpecify: option.requiresSpecify,
    enabled: option.enabled,
    sortOrder: index,
  }));

export const validateSourceOfFundOptions = (options) => {
  const normalized = normalizeSourceOfFundOptions(options);
  const keys = new Set();

  for (const option of normalized) {
    if (keys.has(option.key)) {
      return `Duplicate source of fund key: ${option.key}`;
    }
    keys.add(option.key);
    if (!option.label) {
      return "Each source of fund option must have a label";
    }
  }

  if (!normalized.length) {
    return "At least one source of fund option is required";
  }

  return null;
};

export const getIncomeSourceSpecifyLabel = (label) => `${label}, please specify`;
